/**
 * OfflineIndicator — Shows sync status in the corner of the screen
 *
 * States:
 *  - Online (green dot) — hidden by default, shown briefly on reconnect
 *  - Offline (red banner) — always visible when offline
 *  - Syncing (pulsing) — shown when draining queue
 *  - Pending (orange badge) — shows pending mutation count
 */
import { useEffect } from 'react';
import { useOfflineStore, initOfflineListeners, syncQueue } from '@/core/offline';

export function OfflineIndicator() {
  const isOnline = useOfflineStore((s) => s.isOnline);
  const syncInProgress = useOfflineStore((s) => s.syncInProgress);
  const pendingCount = useOfflineStore((s) => s.pendingCount);
  const lastError = useOfflineStore((s) => s.lastError);

  // Initialize listeners on mount
  useEffect(() => {
    const cleanup = initOfflineListeners();
    return cleanup;
  }, []);

  // If online and no pending items and no error, show nothing
  if (isOnline && pendingCount === 0 && !lastError && !syncInProgress) {
    return null;
  }

  return (
    <div className="fixed bottom-20 sm:bottom-4 right-4 z-50">
      {/* Offline banner */}
      {!isOnline && (
        <div className="flex items-center gap-2 rounded-lg bg-destructive/90 px-3 py-2 text-sm text-white shadow-lg">
          <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
          <span>离线模式</span>
          {pendingCount > 0 && (
            <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-xs">{pendingCount} 待同步</span>
          )}
        </div>
      )}

      {/* Syncing indicator */}
      {isOnline && syncInProgress && (
        <div className="flex items-center gap-2 rounded-lg bg-primary/90 px-3 py-2 text-sm text-white shadow-lg">
          <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
          <span>同步中...</span>
        </div>
      )}

      {/* Pending items badge (online but has queued items) */}
      {isOnline && !syncInProgress && pendingCount > 0 && (
        <button type="button"
          onClick={() => syncQueue()}
          className="flex items-center gap-2 rounded-lg bg-orange-500/90 px-3 py-2 text-sm text-white shadow-lg hover:bg-orange-600/90 transition-colors"
        >
          <span className="h-2 w-2 rounded-full bg-white" />
          <span>{pendingCount} 待同步</span>
          <span className="text-xs underline">点击同步</span>
        </button>
      )}

      {/* Sync error */}
      {lastError && (
        <div className="mt-2 max-w-xs rounded-lg bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
          同步失败: {lastError}
        </div>
      )}
    </div>
  );
}